import { useState } from "react";
import { FlaskConical, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDemoCompany } from "@/hooks/useDemoCompany";
import { useAuth } from "@/contexts/AuthContext";
import { exitDemo } from "@/lib/exitDemo";
import { isSuperAdminEmail } from "@/hooks/useSuperAdminAccess";

/**
 * Permanent DEMO indicator shown on every authenticated screen while the active
 * company is the isolated demo tenant, with the always-visible "Exit Demo"
 * action. Isolation itself is enforced in the database and edge functions; this
 * component only presents the state and triggers `exitDemo()`.
 */
export function DemoEnvironmentBadge() {
  const { isDemo, companyId } = useDemoCompany();
  const { session, markManualLogout } = useAuth();
  const [leaving, setLeaving] = useState(false);

  // A Super Admin is never "in demo mode" — not because the Demo Company
  // exists, not because demo data or a demo membership exists, and not even if
  // the demo tenant happens to be the active company. The badge and its
  // Exit Demo action are for genuine demo users only.
  if (isSuperAdminEmail(session?.user?.email)) return null;

  // Defensive: the badge is an authenticated-only indicator. Never render it on
  // the public sign-in screen, whatever tenant state is still held in memory.
  if (!session?.user) return null;

  if (!isDemo || !companyId) return null;

  const handleExit = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      const outcome = await exitDemo(companyId, { markManualLogout });
      // Full page load: guarantees no demo data survives in memory and that a
      // refresh, a new tab or browser Back cannot restore the demo view.
      window.location.replace(outcome.redirectTo);
    } catch (e) {
      setLeaving(false);
      toast.error("Could not exit the demo", {
        description: e instanceof Error ? e.message : "Please try again.",
      });
    }
  };

  return (
    <div className="fixed bottom-20 left-1/2 z-[9998] md:bottom-3 -translate-x-1/2 print:hidden">
      <div className="flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/15 px-3 py-1.5 text-[11px] font-medium text-amber-700 shadow-sm backdrop-blur dark:text-amber-300">
        <FlaskConical className="h-3.5 w-3.5 shrink-0" />
        <span className="font-semibold tracking-wide">DEMO ENVIRONMENT</span>
        <span className="hidden font-normal opacity-90 sm:inline">
          Sample data only — nothing is sent or billed.
        </span>
        <button
          onClick={handleExit}
          disabled={leaving}
          className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-70 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
        >
          {leaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
          {leaving ? "Exiting…" : "Exit Demo"}
        </button>
      </div>
    </div>
  );
}
